import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { PlatformUpcomingBirthdayDto } from '@data/_generated-api/platform-api/models/platform-upcoming-birthday-dto';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/**
 * Venster van 7 dagen met vandaag als middelste dag (dag 4): drie dagen terug
 * voor net gemiste verjaardagen, drie dagen vooruit voor de komende. Een latere
 * uitbreiding kan dit venster instelbaar maken.
 */
const DAYS_BEFORE = 3;
const DAYS_AHEAD = 3;

/** Wie is er rond vandaag jarig? Klik op een rij laadt die chart direct. */
@Component({
  selector: 'hd-chart-birthdays-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './hd-chart-birthdays-dialog.component.html',
  styleUrl: './hd-chart-birthdays-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartBirthdaysDialogComponent {
  private readonly groupService = inject(HdChartGroupService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  /** Klik op een rij: chart openen (de manager doet de load). */
  readonly chartOpenRequested = output<string>();

  protected readonly items = signal<PlatformUpcomingBirthdayDto[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal('');

  constructor() {
    effect(() => {
      if (this.open()) void this.load();
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    const dayFormat = new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' });
    return {
      title: translate('humanDesign.chartManager.birthdays.title'),
      empty: translate('humanDesign.chartManager.birthdays.empty'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      range: this.transloco.translate<string>(
        'humanDesign.chartManager.birthdays.range',
        {
          from: dayFormat.format(moment().subtract(DAYS_BEFORE, 'days').toDate()),
          to: dayFormat.format(moment().add(DAYS_AHEAD, 'days').toDate()),
        },
        language,
      ),
    };
  });

  protected onOpenChange(open: boolean): void {
    this.open.set(open);
  }

  protected formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('DD-MM') : '—';
  }

  protected turnsText(item: PlatformUpcomingBirthdayDto): string {
    this.contentLang.translationsLoadedTick();
    // Een net gemiste verjaardag is al bereikt: "werd" in plaats van "wordt".
    const passed = !!item.nextBirthdayDate && moment(item.nextBirthdayDate).isBefore(moment(), 'day');
    return this.transloco.translate<string>(
      passed ? 'humanDesign.chartManager.birthdays.turned' : 'humanDesign.chartManager.birthdays.turns',
      { age: item.turnsAge },
      this.contentLang.effectiveLang(),
    );
  }

  protected openChart(item: PlatformUpcomingBirthdayDto): void {
    if (!item.id) return;
    this.chartOpenRequested.emit(item.id);
    this.open.set(false);
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    try {
      this.items.set(await this.groupService.getUpcomingBirthdays(DAYS_AHEAD, DAYS_BEFORE));
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }
}
